import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Content, Dialog, DialogContainer, Divider, Heading } from '@adobe/react-spectrum';

import {
    buildEntrySynergyGraph,
    getAvailableInputsForConsumer,
    pickDefaultPairing,
    SYNERGY_CONSUMER_CATEGORIES,
    SYNERGY_INPUT_GROUPS,
} from '../../../data/buildSynergyGraph';
import { synergyCategoryColor, SYNERGY_CATEGORY_SHORT_LABELS } from '../../../data/synergyCategoryConstants';
import { formatInt, formatPct } from '../../common/cardStats/formatters';
import { synergyEnumLabel } from '../../../data/deckSynergyProfileMarkers';
import '../../common/searedDialog/searedDialog.css';
import './SynergyWebModal.css';

const FINDER_RESULT_LIMIT = 12;

const SynergyWeb3D = lazy(() => import('./SynergyWeb3D'));

const DEFAULT_VIEW_WIDTH = 1400;
const DEFAULT_VIEW_HEIGHT = 800;
const MOBILE_BREAKPOINT = 620;
const MIN_NODES = 8;
const MAX_NODES = 100;

export function SynergyWebModal({ isOpen, onClose, synergyList, cards, profile }) {
    const [nodeLimit, setNodeLimit] = useState(MAX_NODES);
    const [userAdjustedLimit, setUserAdjustedLimit] = useState(false);
    // Two-tier focus: `hoverId` is transient (clears on mouseout), `pinnedId`
    // is sticky (only clears on explicit unpin). Rendering uses the pinned id
    // when one exists so the camera can roam without losing the highlight.
    const [hoverId, setHoverId] = useState(null);
    const [pinnedId, setPinnedId] = useState(null);
    const [consumer, setConsumer] = useState('all');
    const [input, setInput] = useState('all');
    const [userPickedPair, setUserPickedPair] = useState(false);
    const [viewSize, setViewSize] = useState({
        width: DEFAULT_VIEW_WIDTH,
        height: DEFAULT_VIEW_HEIGHT,
    });
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
        return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
    });
    const stageRef = useRef(null);
    const webRef = useRef(null);

    const activeId = pinnedId || hoverId;

    const togglePin = useCallback((id) => {
        setPinnedId((prev) => (prev === id ? null : id));
    }, []);

    const clearPin = useCallback(() => {
        setPinnedId(null);
        setHoverId(null);
    }, []);

    const focusAndPin = useCallback((id) => {
        webRef.current?.focusNode(id);
        setPinnedId(id);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
        const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
        const update = (e) => setIsMobile(e.matches);
        setIsMobile(mql.matches);
        if (mql.addEventListener) mql.addEventListener('change', update);
        else mql.addListener(update);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener('change', update);
            else mql.removeListener(update);
        };
    }, []);

    // When a pin is active, ESC unlocks it instead of dismissing the modal.
    // Capture-phase so we intercept before React Spectrum's dialog close.
    useEffect(() => {
        if (!isOpen || !pinnedId) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setPinnedId(null);
            }
        };
        document.addEventListener('keydown', onKey, true);
        return () => document.removeEventListener('keydown', onKey, true);
    }, [isOpen, pinnedId]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const el = stageRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const apply = (w, h) => {
            if (w <= 0 || h <= 0) return;
            setViewSize((prev) =>
                Math.abs(prev.width - w) < 2 && Math.abs(prev.height - h) < 2 ? prev : { width: w, height: h }
            );
        };
        apply(Math.round(el.clientWidth), Math.round(el.clientHeight));
        const ro = new ResizeObserver((entries) => {
            const rect = entries[0].contentRect;
            apply(Math.round(rect.width), Math.round(rect.height));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [isOpen]);

    const availableNodeCount = useMemo(() => {
        if (!synergyList || typeof synergyList !== 'object') return MIN_NODES;
        const count = Object.keys(synergyList).length;
        return Math.max(MIN_NODES, Math.min(MAX_NODES, count));
    }, [synergyList]);

    useEffect(() => {
        if (userAdjustedLimit) return;
        setNodeLimit(availableNodeCount);
    }, [availableNodeCount, userAdjustedLimit]);

    // Default the consumer/input pair to whichever pairing dominates this
    // deck — if the user hasn't touched the dropdowns yet.
    useEffect(() => {
        if (userPickedPair) return;
        const defaults = pickDefaultPairing(synergyList);
        setConsumer(defaults.consumer);
        setInput(defaults.input);
    }, [synergyList, userPickedPair]);

    // When the consumer changes, validate the current input against what's
    // actually available for that consumer. If nothing's available, fall
    // back to 'all' so the graph doesn't go empty.
    const availableInputs = useMemo(
        () => getAvailableInputsForConsumer(synergyList, consumer),
        [synergyList, consumer]
    );
    useEffect(() => {
        if (availableInputs.has(input)) return;
        setInput('all');
    }, [availableInputs, input]);

    const effectiveLimit = Math.min(nodeLimit, availableNodeCount);
    const graph = useMemo(
        () => buildEntrySynergyGraph(synergyList, { nodeLimit: effectiveLimit, consumer, input }),
        [synergyList, effectiveLimit, consumer, input]
    );

    const neighbors = useMemo(() => {
        const map = new Map();
        graph.nodes.forEach((n) => map.set(n.id, new Set([n.id])));
        graph.edges.forEach((e) => {
            map.get(e.source)?.add(e.target);
            map.get(e.target)?.add(e.source);
        });
        return map;
    }, [graph]);

    const maxScore = useMemo(() => Math.max(1, ...graph.nodes.map((n) => n.score || 0)), [graph]);
    const maxWeight = useMemo(() => Math.max(1, ...graph.edges.map((e) => e.weight || 0)), [graph]);

    const cardNodes = useMemo(() => graph.nodes.filter((n) => n.kind === 'card'), [graph]);
    const cardNodeCount = cardNodes.length;
    const entryNodeCount = graph.nodes.length - cardNodeCount;

    if (!isOpen) return null;

    const shapeLabel = [
        synergyEnumLabel('webTopology', profile?.webTopology),
        synergyEnumLabel('concentration', profile?.concentration),
    ]
        .filter(Boolean)
        .join(' · ');

    const markPairTouched = () => setUserPickedPair(true);

    return (
        <DialogContainer type="modal" isDismissable onDismiss={onClose}>
            <Dialog
                width="calc(100vw - 32px)"
                height="calc(100vh - 48px)"
                UNSAFE_className="searedDialog synergyWebDialog"
            >
                <Heading>
                    <span className="searedDialog__eyebrow">Synergy web</span>
                    <span className="searedDialog__title">{shapeLabel || 'Synergy dependencies'}</span>
                    <span className="searedDialog__subtitle">
                        Each card is a node — edges connect cards that trigger or enable each other. Size tracks score
                        contribution; line color tracks the source entry's category.
                    </span>
                </Heading>
                <Divider />
                <Content>
                    <div className="synergyWebBody">
                        <div className="synergyWebToolbar">
                            <div className="synergyWebToolbarMetric synergyWebPairFilter">
                                <span className="synergyWebToolbarLabel">Show</span>
                                <select
                                    className="synergyWebRefFilter"
                                    value={input}
                                    onChange={(e) => {
                                        markPairTouched();
                                        setInput(e.target.value);
                                    }}
                                    aria-label="Input group"
                                >
                                    {Object.entries(SYNERGY_INPUT_GROUPS).map(([key, def]) => (
                                        <option key={key} value={key} disabled={!availableInputs.has(key)}>
                                            {def.label}
                                        </option>
                                    ))}
                                </select>
                                <span className="synergyWebPairArrow" aria-hidden>
                                    →
                                </span>
                                <select
                                    className="synergyWebRefFilter"
                                    value={consumer}
                                    onChange={(e) => {
                                        markPairTouched();
                                        setConsumer(e.target.value);
                                    }}
                                    aria-label="Consumer entry category"
                                >
                                    {Object.entries(SYNERGY_CONSUMER_CATEGORIES).map(([key, def]) => (
                                        <option key={key} value={key}>
                                            {def.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {profile?.gini != null && (
                                <div className="synergyWebToolbarMetric">
                                    <span className="synergyWebToolbarLabel">Gini</span>
                                    <span className="synergyWebToolbarValue">{profile.gini.toFixed(2)}</span>
                                    {profile.topConditionShare != null && (
                                        <>
                                            <span className="synergyWebToolbarSep">·</span>
                                            <span className="synergyWebToolbarLabel">Top entry</span>
                                            <span className="synergyWebToolbarValue">
                                                {formatPct(profile.topConditionShare)}
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}

                            <div className="synergyWebToolbarGesture synergyWebToolbarHint">
                                drag to rotate · scroll to zoom · shift-drag to pan
                            </div>

                            <div className="synergyWebToolbarMetric synergyWebToolbarMetric--right">
                                <span className="synergyWebToolbarLabel">Top</span>
                                <input
                                    type="range"
                                    min={Math.min(MIN_NODES, availableNodeCount)}
                                    max={availableNodeCount}
                                    value={effectiveLimit}
                                    onChange={(e) => {
                                        setUserAdjustedLimit(true);
                                        setNodeLimit(parseInt(e.target.value, 10));
                                    }}
                                    className="synergyWebSlider"
                                    aria-label="Number of nodes to show"
                                />
                                <span className="synergyWebToolbarValue">{effectiveLimit}</span>
                                <span className="synergyWebToolbarHint">of {availableNodeCount} cards</span>
                            </div>
                        </div>

                        {graph.nodes.length === 0 ? (
                            <div className="synergyWebEmpty">No synergy entries available to graph for this deck.</div>
                        ) : (
                            <>
                                {isMobile && (
                                    <SynergyWebLegend
                                        categories={Array.from(
                                            new Set(graph.nodes.map((n) => n.category).filter(Boolean))
                                        )}
                                        inline
                                    />
                                )}
                                <div className="synergyWebStage synergyWebStage--webgl" ref={stageRef}>
                                    <Suspense fallback={<SynergyWebLoading />}>
                                        <SynergyWeb3D
                                            ref={webRef}
                                            graph={graph}
                                            cards={cards}
                                            maxScore={maxScore}
                                            maxWeight={maxWeight}
                                            viewSize={viewSize}
                                            hoveredId={activeId}
                                            onHover={setHoverId}
                                            onSelect={togglePin}
                                            onBackgroundClick={clearPin}
                                            neighbors={neighbors}
                                        />
                                    </Suspense>

                                    {cardNodes.length > 0 && (
                                        <SynergyWebFinder
                                            cardNodes={cardNodes}
                                            cards={cards}
                                            activeId={activeId}
                                            pinnedId={pinnedId}
                                            onSelect={focusAndPin}
                                        />
                                    )}

                                    {!isMobile && (
                                        <SynergyWebLegend
                                            categories={Array.from(
                                                new Set(graph.nodes.map((n) => n.category).filter(Boolean))
                                            )}
                                        />
                                    )}

                                    <div className="synergyWebCountOverlay" aria-live="polite">
                                        <span className="synergyWebToolbarLabel">Showing</span>
                                        <span className="synergyWebToolbarValue">{cardNodeCount}</span>
                                        <span className="synergyWebToolbarHint">cards</span>
                                        {entryNodeCount > 0 && (
                                            <>
                                                <span className="synergyWebToolbarSep">·</span>
                                                <span className="synergyWebToolbarValue">{entryNodeCount}</span>
                                                <span className="synergyWebToolbarHint">entries</span>
                                            </>
                                        )}
                                        <span className="synergyWebToolbarSep">·</span>
                                        <span className="synergyWebToolbarValue">{graph.edges.length}</span>
                                        <span className="synergyWebToolbarHint">edges</span>
                                    </div>

                                    {activeId &&
                                        (() => {
                                            const activeNode = graph.nodes.find((n) => n.id === activeId);
                                            if (!activeNode) return null;
                                            const resolvedCardId =
                                                activeNode.kind === 'entry' ? activeNode.cardId : activeId;
                                            return (
                                                <SynergyWebHoverCard
                                                    node={activeNode}
                                                    card={cards?.[resolvedCardId]}
                                                    maxScore={maxScore}
                                                    neighborCount={(neighbors.get(activeId)?.size || 1) - 1}
                                                    isPinned={pinnedId === activeId}
                                                    onUnpin={clearPin}
                                                />
                                            );
                                        })()}
                                </div>
                            </>
                        )}
                    </div>
                </Content>
            </Dialog>
        </DialogContainer>
    );
}

function SynergyWebFinder({ cardNodes, cards, activeId, pinnedId, onSelect }) {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [highlight, setHighlight] = useState(0);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);

    // Name-annotated list, stable across renders of the same graph.
    const annotated = useMemo(
        () =>
            cardNodes.map((n) => ({
                node: n,
                name: cards?.[n.id]?.name || n.id,
            })),
        [cardNodes, cards]
    );

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = q ? annotated.filter((r) => r.name.toLowerCase().includes(q)) : annotated.slice();
        filtered.sort((a, b) => (b.node.score || 0) - (a.node.score || 0));
        return filtered.slice(0, FINDER_RESULT_LIMIT);
    }, [annotated, query]);

    // Clamp highlight whenever the result set shrinks (e.g. as the user types).
    useEffect(() => {
        setHighlight((h) => {
            if (results.length === 0) return 0;
            if (h >= results.length) return results.length - 1;
            return h;
        });
    }, [results]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onDocMouseDown = (e) => {
            if (!containerRef.current?.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        return () => document.removeEventListener('mousedown', onDocMouseDown);
    }, [isOpen]);

    // Keep the highlighted row scrolled into view as the user arrows down.
    useEffect(() => {
        if (!isOpen || !listRef.current) return;
        const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ block: 'nearest' });
        }
    }, [highlight, isOpen]);

    const pick = useCallback(
        (idx) => {
            const hit = results[idx];
            if (!hit) return;
            onSelect(hit.node.id);
            setIsOpen(false);
            setQuery('');
            inputRef.current?.blur();
        },
        [results, onSelect]
    );

    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            inputRef.current?.blur();
            return;
        }
        if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
            setIsOpen(true);
            return;
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlight((h) => Math.min(results.length - 1, h + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlight((h) => Math.max(0, h - 1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            pick(highlight);
        }
    };

    return (
        <div className="synergyWebFinder" ref={containerRef}>
            <span className="synergyWebFinderIcon" aria-hidden>
                ⌕
            </span>
            <input
                ref={inputRef}
                type="search"
                className="synergyWebFinderInput"
                placeholder="Find card…"
                value={query}
                onChange={(e) => {
                    setQuery(e.target.value);
                    setIsOpen(true);
                    setHighlight(0);
                }}
                onFocus={() => setIsOpen(true)}
                onKeyDown={onKeyDown}
                aria-label="Find a card in the synergy web"
                aria-expanded={isOpen}
                aria-autocomplete="list"
                aria-controls="synergyWebFinderList"
            />
            {isOpen && (
                <div className="synergyWebFinderPanel" role="listbox" id="synergyWebFinderList" ref={listRef}>
                    {results.length === 0 ? (
                        <div className="synergyWebFinderEmpty">No matches</div>
                    ) : (
                        results.map(({ node, name }, idx) => (
                            <button
                                key={node.id}
                                type="button"
                                data-idx={idx}
                                role="option"
                                aria-selected={idx === highlight || node.id === activeId}
                                className={`synergyWebFinderItem${
                                    idx === highlight ? ' synergyWebFinderItem--active' : ''
                                }${node.id === pinnedId ? ' synergyWebFinderItem--pinned' : ''}`}
                                onMouseEnter={() => setHighlight(idx)}
                                onMouseDown={(e) => {
                                    // mousedown (not click) fires before the
                                    // outside-click handler closes the panel.
                                    e.preventDefault();
                                    pick(idx);
                                }}
                            >
                                <span
                                    className="synergyWebFinderSwatch"
                                    style={{ background: synergyCategoryColor(node.category) }}
                                />
                                <span className="synergyWebFinderName">{name}</span>
                                <span className="synergyWebFinderScore">{formatInt(node.score)}</span>
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

function SynergyWebLoading() {
    return (
        <div className="synergyWebLoading" role="status" aria-live="polite">
            <div className="synergyWebLoadingPulse" />
            <span className="synergyWebLoadingLabel">Spinning up WebGL…</span>
        </div>
    );
}

function SynergyWebLegend({ categories, inline }) {
    if (!categories || categories.length === 0) return null;
    return (
        <div className={`synergyWebLegend${inline ? ' synergyWebLegend--inline' : ''}`}>
            {categories.map((cat) => (
                <div key={cat} className="synergyWebLegendItem">
                    <span
                        className="synergyWebLegendSwatch synergyWebLegendSwatch--line"
                        style={{ background: synergyCategoryColor(cat) }}
                    />
                    <span className="synergyWebLegendLabel">{SYNERGY_CATEGORY_SHORT_LABELS[cat] || cat}</span>
                </div>
            ))}
        </div>
    );
}

function SynergyWebHoverCard({ node, card, maxScore, neighborCount, isPinned, onUnpin }) {
    if (!node) return null;
    const isEntry = node.kind === 'entry';
    const name = card?.name || node.cardId || node.id;
    const sharePct = Math.round(((node.score || 0) / Math.max(1, maxScore)) * 100);
    const categoryLabel = SYNERGY_CATEGORY_SHORT_LABELS[node.category] || node.category;
    return (
        <div
            className={`synergyWebHoverCard${isPinned ? ' synergyWebHoverCard--pinned' : ''}`}
            role="status"
            aria-live="polite"
        >
            {card?.imageUri && <img src={card.imageUri} alt={name} className="synergyWebHoverImage" />}
            <div className="synergyWebHoverBody">
                {isPinned && (
                    <button
                        type="button"
                        className="synergyWebHoverLock"
                        onClick={onUnpin}
                        aria-label="Unlock selection"
                        title="Click to unlock · ESC also works"
                    >
                        <span aria-hidden>🔒</span>
                        <span className="synergyWebHoverLockLabel">Locked</span>
                        <span className="synergyWebHoverLockClose" aria-hidden>
                            ×
                        </span>
                    </button>
                )}
                {isEntry ? (
                    <>
                        <span className="synergyWebHoverEyebrow" style={{ color: synergyCategoryColor(node.category) }}>
                            {categoryLabel}
                        </span>
                        <span className="synergyWebHoverName">{name}</span>
                        {node.text && <span className="synergyWebHoverText">{node.text}</span>}
                    </>
                ) : (
                    <span className="synergyWebHoverName">{name}</span>
                )}
                <div className="synergyWebHoverMeta">
                    <span>
                        <strong>{formatInt(node.score)}</strong> pts
                    </span>
                    {!isEntry && (
                        <span>
                            <strong>{node.entryCount}</strong> entries
                        </span>
                    )}
                    <span>
                        <strong>{neighborCount}</strong> connections
                    </span>
                    <span>
                        <strong>{sharePct}%</strong> of top
                    </span>
                </div>
                <span className="synergyWebHoverHint">
                    {isPinned
                        ? 'Locked · click the card, background, or ESC to unlock'
                        : 'Click to lock the selection · pivots the camera here'}
                </span>
            </div>
        </div>
    );
}
